import { PlayCircleOutlined } from '@ant-design/icons'
import { Button, Dropdown, MenuProps, Modal, notification } from 'antd'
import { FC } from 'react'
import {
    shipmentsApi,
    useExecuteShipmentMaterialMutation,
    useExecuteShipmentMutation,
    useLazyGetShipmentByDocIdQuery,
} from '../../../../../../store/api/shipments-api'
import useErrorModal from '../../../../../../hooks/api/use-error-modal'
import useEndpointQueryParams from '../../../../../../hooks/table-pages/use-endpoint-query-parameters'
import { defaultParams } from '../../../../constants/default-params'
import { deserializeFilters } from '../../../../helpers/deserialize-filters'
import { useAppDispatch } from '../../../../../../store/store'
import { DataSourceItem } from '../../../../shipments-page'
import { useIsShipmentStarted } from '../../../shipment-form/components/form-items/material-requirements-wrapper/hooks/use-is-shipment-started'

interface RunShipmentProps {
    data: DataSourceItem
}

const RunShipment: FC<RunShipmentProps> = ({ data }) => {
    const dispatch = useAppDispatch()
    const params = useEndpointQueryParams({ deserializeFilters })
    const queryArg = { ...params, ...defaultParams }
    const [execute, executeState] = useExecuteShipmentMutation()
    const [executeMaterials, executeMaterialsState] = useExecuteShipmentMaterialMutation()
    const [getShipmentByDocId, getShipmentByDocIdState] = useLazyGetShipmentByDocIdQuery()
    const isShipmentStarted = useIsShipmentStarted(data)

    useErrorModal(getShipmentByDocIdState, 'Ошибка получения данных отгрузки')

    const updateShipmentInQueryCache = (shipment: DataSourceItem) => {
        const action = shipmentsApi.util.updateQueryData('getShipments', queryArg, draft => {
            const index = draft.data.findIndex(item => item.documentId === shipment.documentId)

            if (index !== -1) {
                draft.data[index] = shipment
            }
        })

        dispatch(action)
    }

    const refetchShipmentAndUpdateCache = async () => {
        const shipmentDocId = data?.documentId

        if (!shipmentDocId) {
            return
        }

        const result = await getShipmentByDocId([shipmentDocId, queryArg])

        if (result.data?.data) {
            updateShipmentInQueryCache(result.data.data)
        }
    }

    const onExecuteShipment = async () => {
        if (isShipmentStarted) {
            notification.warning({ message: 'Отгрузка уже запущена' })
            return
        }
        const result = await execute(data?.documentId || '')
        if (result.data) {
            notification.success({ message: 'Отгрузка запущена' })
            await refetchShipmentAndUpdateCache()
        } else {
            Modal.error({ title: 'Ошибка запуска отгрузки', content: JSON.stringify(result.error) })
        }
    }

    const onExecuteShipmentMaterials = async () => {
        const result = await executeMaterials(data?.documentId || '')
        if (result.data) {
            notification.success({ message: 'Задачи и резервации по материалам обработаны' })
        } else {
            Modal.error({
                title: 'Ошибка обработки требуемых материалов',
            })
        }
    }

    const items: MenuProps['items'] = isShipmentStarted
        ? []
        : [
              {
                  key: 'execute',
                  label: 'Запустить отгрузку',
                  onClick: onExecuteShipment,
              },
              {
                  key: 'materials',
                  label: 'Создать ТОЛЬКО заявку на заказ материалов',
                  onClick: onExecuteShipmentMaterials,
              },
          ]

    return (
        <Dropdown menu={{ items }}>
            <Button
                size={'small'}
                icon={<PlayCircleOutlined />}
                color={'blue'}
                variant={'solid'}
                disabled={isShipmentStarted}
                title={isShipmentStarted ? 'Отгрузка уже запущена' : ''}
                loading={
                    executeState.isLoading ||
                    executeMaterialsState.isLoading ||
                    getShipmentByDocIdState.isLoading ||
                    getShipmentByDocIdState.isFetching
                }
            />
        </Dropdown>
    )
}

export default RunShipment
